import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/api/settings';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Billing → Authorization Limits. Thresholds that drive the
 * authorization warnings on the schedule and dashboard: how many days before
 * expiry counts as "expiring" vs "urgent", and at what utilization a low-hours
 * alert fires. `authBlock` decides whether scheduling against an exhausted
 * authorization is hard-blocked or merely warned.
 *
 * Lands in settings.billing. Shallow-merge patch so the unrelated OA keys
 * (oaSubmitterId etc.) stay intact.
 */

interface FormState {
    authExpiryDays: string;
    authUrgentDays: string;
    authLowPct: string;
    authBlock: 'soft' | 'hard';
}

const EMPTY: FormState = {
    authExpiryDays: '30',
    authUrgentDays: '7',
    authLowPct: '10',
    authBlock: 'hard',
};

export function AuthorizationLimitsPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.billing) return;
        const b = data.billing;
        setForm({
            authExpiryDays: typeof b.authExpiryDays === 'number' ? String(b.authExpiryDays) : '30',
            authUrgentDays: typeof b.authUrgentDays === 'number' ? String(b.authUrgentDays) : '7',
            authLowPct: typeof b.authLowPct === 'number' ? String(b.authLowPct) : '10',
            authBlock: b.authBlock === 'soft' ? 'soft' : 'hard',
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);

        const expiryNum = Number(form.authExpiryDays);
        const urgentNum = Number(form.authUrgentDays);
        const lowPctNum = Number(form.authLowPct);

        if (!Number.isInteger(expiryNum) || expiryNum < 0 || expiryNum > 365) {
            setErr('Expiring-soon window must be 0–365 days.');
            return;
        }
        if (!Number.isInteger(urgentNum) || urgentNum < 0 || urgentNum > 365) {
            setErr('Urgent window must be 0–365 days.');
            return;
        }
        if (urgentNum > expiryNum) {
            setErr('Urgent window must be at or below the expiring-soon window.');
            return;
        }
        if (!Number.isInteger(lowPctNum) || lowPctNum < 0 || lowPctNum > 100) {
            setErr('Low-hours threshold must be 0–100 percent.');
            return;
        }

        try {
            await updateM.mutateAsync({
                type: 'billing',
                updates: {
                    authExpiryDays: expiryNum,
                    authUrgentDays: urgentNum,
                    authLowPct: lowPctNum,
                    authBlock: form.authBlock,
                },
            });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Authorization Limits</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Thresholds that drive the authorization warnings. "Expiring soon" and
                "urgent" are measured against the authorization end date; "low hours"
                is the percentage of authorized hours still remaining.
            </p>

            <Card className="p-5 max-w-3xl">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-3 gap-3">
                        <FormField label="Expiring soon (days)" hint="Yellow banner warning">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.authExpiryDays}
                                    onChange={(e) => set('authExpiryDays', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                    inputMode="numeric"
                                    placeholder="30"
                                />
                            )}
                        </FormField>
                        <FormField label="Urgent notification (days)" hint="Red banner warning">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.authUrgentDays}
                                    onChange={(e) => set('authUrgentDays', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                    inputMode="numeric"
                                    placeholder="7"
                                />
                            )}
                        </FormField>
                        <FormField label="Low hours (%)" hint="Fires when remaining / authorized drops below this">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.authLowPct}
                                    onChange={(e) => set('authLowPct', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                    inputMode="numeric"
                                    placeholder="10"
                                />
                            )}
                        </FormField>
                    </div>

                    <FormField
                        label="When authorized hours are exhausted"
                        hint="Soft only warns. Hard prevents the session from saving."
                    >
                        {(id) => (
                            <select
                                id={id}
                                value={form.authBlock}
                                onChange={(e) => set('authBlock', e.target.value === 'soft' ? 'soft' : 'hard')}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                <option value="hard">Hard block — prevent scheduling</option>
                                <option value="soft">Soft warn — allow with warning</option>
                            </select>
                        )}
                    </FormField>

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-end">
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
